/**
 * Phase 136B — Replay Expiry Countdown UX
 * ----------------------------------------
 * Cinematic countdown banners that turn replay retention into the platform's
 * strongest organic upgrade nudge.
 *
 * Three states (from `replay.expiry_meta.expiry_state`):
 *   • pro_archived   — calm green "Archived Safely" pill.
 *   • pilot_safe     — subtle amber pill "Archive expires in N days".
 *   • pilot_urgent   — emotional reframe "Preserve This Match" with upgrade CTA.
 *
 * Rule: NEVER block playback. NEVER feel like panic marketing.
 *       Just let emotional attachment build naturally.
 */
import { Link } from 'react-router-dom';
import { Clock, ShieldCheck, Sparkles, Heart } from 'lucide-react';

export function ReplayExpiryPill({ meta }) {
  if (!meta) return null;
  const { expiry_state, days_until_expiry } = meta;
  if (expiry_state === 'pro_archived') {
    return (
      <span
        data-testid="livex-replay-archive-pro"
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] rounded"
        style={{
          background: 'linear-gradient(135deg, rgba(16,185,129,0.18), rgba(16,185,129,0.06))',
          color: '#6ee7b7',
          border: '1.5px solid rgba(16,185,129,0.45)',
        }}
      >
        <ShieldCheck className="h-2.5 w-2.5" /> Archived Safely
      </span>
    );
  }
  if (expiry_state === 'purged') {
    return (
      <span
        data-testid="livex-replay-archive-purged"
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] rounded bg-[#0c1a2e] border-2 border-rose-900 text-rose-300"
      >
        <Clock className="h-2.5 w-2.5" /> Retention expired
      </span>
    );
  }
  if (expiry_state === 'pilot_safe') {
    const label = days_until_expiry == null
      ? 'Pilot retention'
      : days_until_expiry <= 0
      ? 'Expires today'
      : days_until_expiry === 1
      ? 'Expires tomorrow'
      : `Archive expires in ${days_until_expiry} days`;
    return (
      <span
        data-testid="livex-replay-archive-pilot-safe"
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] rounded"
        style={{
          background: 'linear-gradient(135deg, rgba(245,158,11,0.18), rgba(245,158,11,0.06))',
          color: '#fcd34d',
          border: '1.5px solid rgba(245,158,11,0.45)',
        }}
      >
        <Clock className="h-2.5 w-2.5" /> {label}
      </span>
    );
  }
  // pilot_urgent — surface only the pill here. The full banner renders separately.
  const urgentLabel = days_until_expiry == null
    ? 'Expiring soon'
    : days_until_expiry <= 0
    ? 'Expires today'
    : days_until_expiry === 1
    ? 'Expires tomorrow'
    : `Expires in ${days_until_expiry} days`;
  return (
    <span
      data-testid="livex-replay-archive-pilot-urgent"
      className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] rounded"
      style={{
        background: 'linear-gradient(135deg, rgba(249,115,22,0.25), rgba(249,115,22,0.10))',
        color: '#fdba74',
        border: '1.5px solid rgba(249,115,22,0.60)',
        boxShadow: '0 0 12px rgba(249,115,22,0.18)',
      }}
    >
      <Heart className="h-2.5 w-2.5" /> {urgentLabel}
    </span>
  );
}

export function ReplayPreserveBanner({ meta, replayTitle = 'this match' }) {
  if (!meta || meta.expiry_state !== 'pilot_urgent') return null;
  const days = meta.days_until_expiry;
  const headline = days == null
    ? 'Preserve This Match'
    : days <= 0
    ? `${replayTitle} expires today — preserve it`
    : days === 1
    ? `${replayTitle} expires tomorrow — preserve it`
    : `Preserve This Match — ${days} days remaining`;
  return (
    <div
      data-testid="livex-replay-preserve-banner"
      className="mt-3 p-3 rounded-xl border-2 flex items-start gap-3"
      style={{
        background: 'linear-gradient(135deg, rgba(249,115,22,0.15), rgba(220,38,38,0.08) 70%, transparent)',
        borderColor: 'rgba(249,115,22,0.55)',
        boxShadow: '0 0 24px rgba(249,115,22,0.12)',
      }}
    >
      <div
        className="h-9 w-9 rounded-lg grid place-items-center flex-shrink-0"
        style={{ background: 'linear-gradient(135deg,#f97316,#dc2626)' }}
      >
        <Heart className="h-4 w-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-black text-white tracking-tight">{headline}</div>
        <div className="text-[12px] font-medium text-orange-100 mt-0.5">
          Keep this replay available for <b>90 days</b> with StreamX Pro. Your academy history stays safe.
        </div>
      </div>
      <Link
        to="/streamx/subscription"
        data-testid="livex-replay-preserve-cta"
        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold text-white rounded-md border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5 flex-shrink-0 self-center"
        style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
      >
        <Sparkles className="h-3 w-3" /> Upgrade to Pro
      </Link>
    </div>
  );
}
